import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_SKIN } from "./skins/default";
import type { SkinDef, SkinSlots } from "./types";

/**
 * Theme context — a thin Zustand-free wrapper exposing the active
 * skin and a setter. Skin swaps go through `setSkin(other)` which
 * re-applies every cssVar onto `document.documentElement`; component
 * trees inherit the change without re-rendering (browser handles
 * the live re-paint via CSS custom property recompute).
 *
 * The provider is intentionally kept Zustand-out — Zustand owns the
 * game state mirror, and theming is a presentation concern that
 * doesn't need persistence / time-travel.
 */
interface ThemeContextValue {
  readonly skin: SkinDef;
  readonly setSkin: (next: SkinDef) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  skin: DEFAULT_SKIN,
  setSkin: () => {
    // no-op fallback for components rendered outside a ThemeProvider
  },
});

/**
 * Write a skin's tokens onto the document root. Call once at
 * bootstrap before the first render, then on every skin swap.
 */
export function applyTokens(skin: SkinDef): void {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(skin.cssVars)) {
    root.style.setProperty(key, value);
  }
}

export interface ThemeProviderProps {
  initial?: SkinDef;
  children: ReactNode;
}

export function ThemeProvider({
  initial,
  children,
}: ThemeProviderProps): JSX.Element {
  const [skin, setSkinState] = useState<SkinDef>(initial ?? DEFAULT_SKIN);

  // `initial` is the skin the HOST wants — it changes when the user
  // picks a palette in settings, so it has to be followed, not just
  // read once at mount. `setSkin` still works for an ad-hoc swap until
  // the host next changes its mind.
  useEffect(() => {
    if (initial !== undefined) setSkinState(initial);
  }, [initial]);

  // Layout effect, not a passive one: the stylesheet reads every
  // visual constant through `var(--token)` with no literal fallback,
  // so a skin that lands after the first paint would flash unstyled.
  // `applyTokens` is also called once at bootstrap before React
  // renders; this hook covers later skin swaps.
  useLayoutEffect(() => {
    applyTokens(skin);
  }, [skin]);

  const setSkin = useCallback((next: SkinDef) => {
    setSkinState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ skin, setSkin }),
    [skin, setSkin],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/**
 * Pull a single component slot out of the active skin, falling back
 * to the default skin's slot if the active skin didn't override it.
 * Components consume the resolved slot directly so the slot's host
 * component never lives outside a `ThemeProvider`.
 */
export function useSkinSlot<K extends keyof SkinSlots>(slot: K): SkinSlots[K] {
  const { skin } = useTheme();
  const override = skin.components?.[slot];
  if (override !== undefined) return override;
  // The default skin always ships every slot — the type cast is
  // safe because `DEFAULT_SKIN.components` is the canonical full set.
  const defaults = DEFAULT_SKIN.components;
  if (defaults === undefined || defaults[slot] === undefined) {
    throw new Error(
      `theme: default skin missing slot "${String(slot)}" — fix the default skin definition`,
    );
  }
  return defaults[slot] as SkinSlots[K];
}
